import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import { MRT_Row, MRT_ColumnDef } from "material-react-table"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { COMPANY_ADMIN_ROUTE } from "../../../../../../constants/routes"
import React, { Suspense, useCallback, useMemo, useState } from "react"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { Button } from "../../../../../office/admin/components/Button"
import { MenuItem } from "@mui/material"
import { CreateBillDialog } from "../components/CreateBillDialog"
import { AccountingBillReceiversResponse, BillReceiversQuery, fetchAccountingBillReceivers } from "../billReceiversApi"
import { BillReceiversItem } from "../billReceiversTypes"
import { useCurrentCompany } from "../../../../hooks/useCurrentCompany"
import { StandardTwoLinesCell } from "../../../../../../shared/components/table/StandardTwoLinesCell"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { billReceiverToApi } from "../../../../../../shared/components/billReceivers/billReceiverToApi"
import { createBillReceivers } from "../../../../../../shared/components/billReceivers/billReceiverApi"

type Props = Readonly<{}>

function transformData(data: AccountingBillReceiversResponse) {
    return data.billReceivers
}

export function BillReceivers({}: Props) {
    const { l10n } = useAppLocalization()

    const { companyId } = useCurrentCompany()
    const [isOpenCreateBill, setIsOpenCreateBill] = useState(false)

    const billReceivers = useApiQuery(fetchAccountingBillReceivers, {
        queryName: BillReceiversQuery.LIST,
        dependencies: {
            companyId,
        },
        select: transformData,
    })

    const { mutate } = useApiMutation(createBillReceivers, {})

    const { ApiQueryTable, columnFactory } = useApiQueryTable(billReceivers)

    const columns = useMemo<MRT_ColumnDef<BillReceiversItem>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("company-accounting-bill-receivers-table-header-debitor-id"),
                accessorKey: "debitorNo",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("company-accounting-bill-receivers-table-header-name"),
                accessorKey: "name",
                Cell: ({ row }) => (
                    <StandardTwoLinesCell title={row.original.name} subtitle1={row.original.addressDisplayText} />
                ),
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("company-accounting-bill-receivers-table-header-status"),
                    accessorKey: "isActive",
                },
                (row) => row?.isActive,
            ),
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<BillReceiversItem>; closeMenu: () => void }) => [
            <MenuItem
                key={`edit-${row.original.id}`}
                onClick={() => {
                    closeMenu()
                }}
            >
                <LocalizedStrict id="company-accounting-bill-receivers-table-button-edit">
                    <span>Edit</span>
                </LocalizedStrict>
            </MenuItem>,
            <MenuItem
                key={`delete-${row.original.id}`}
                onClick={() => {
                    closeMenu()
                }}
            >
                <LocalizedStrict id="company-accounting-bill-receivers-table-button-delete">
                    <span>Delete</span>
                </LocalizedStrict>
            </MenuItem>,
        ],
        [],
    )

    return (
        <>
            <LocalizedStrict id="company-accounting-bill-receivers-table-header-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.COMPANY_ACCOUNTING_BILL_RECEIVERS}
                    title="Bill receivers"
                    icon={<FontAwesomeIcon icon={faShop} />}
                    rightElement={
                        <LocalizedStrict id="company-accounting-bill-receivers-button-create">
                            <Button variant="contained" onClick={() => setIsOpenCreateBill(true)}>
                                Create
                            </Button>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-accounting-bill-receivers-table-header-nav-item-home">
                            <BreadcrumbLink to={COMPANY_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-accounting-bill-receivers-table-header-nav-item-accounting">
                            <Breadcrumb>Accounting</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-accounting-bill-receivers-table-header-nav-item-bill-receivers">
                            <Breadcrumb>Bill receivers</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <ApiQueryTable
                        columns={columns}
                        initialState={{ density: "spacious" }}
                        renderRowActionMenuItems={renderRowActionMenuItems}
                    />
                </Suspense>
            </QueryErrorBoundary>

            {isOpenCreateBill && (
                <CreateBillDialog
                    onSelectBillReceiver={(formValues) => {
                        const endpointType = formValues.companySendInvoice
                        const endpointValue = formValues.search
                        const billType = formValues.type

                        const apiRequestBody = billReceiverToApi(formValues, billType, endpointValue, endpointType)

                        mutate({ billReceiver: apiRequestBody?.billReceiver })
                    }}
                    companyId={companyId}
                    open={isOpenCreateBill}
                    onClose={() => setIsOpenCreateBill(false)}
                    title={l10n.getString("bill-receivers-create-dialog-title")}
                />
            )}
        </>
    )
}
